import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "../DataContext";
import axios from "axios";

const API_BASE      = "https://pmo-platform.onrender.com/api/ms-reports";
const HISTORY_KEY   = "pmo_ms_report_history";
const MAX_HISTORY   = 60;

// ── 19 MS Project reports organised by category ────────────────────────────
const CATEGORIES = [
  {
    key:   "Dashboards",
    label: "Dashboards",
    icon:  "⬛",
    color: "#1F3864",
    description: "High-level project health dashboards equivalent to MS Project's built-in dashboard reports",
    reports: [
      {
        key:  "project_overview",
        id:   "D-01",
        name: "Project Overview",
        desc: "Phase completion bars · milestone table · late task count KPIs",
        ms:   "Report › Dashboards › Project Overview",
      },
      {
        key:  "burndown",
        id:   "D-02",
        name: "Burndown Chart",
        desc: "Work remaining vs baseline · task count burndown over reporting periods",
        ms:   "Report › Dashboards › Burndown",
      },
      {
        key:  "cost_overview",
        id:   "D-03",
        name: "Cost Overview",
        desc: "Cost by phase (stacked column) · cumulative cost line overlay",
        ms:   "Report › Dashboards › Cost Overview",
      },
      {
        key:  "work_overview",
        id:   "D-04",
        name: "Work Overview",
        desc: "Work hours by resource · work by phase — grouped bars",
        ms:   "Report › Dashboards › Work Overview",
      },
      {
        key:  "upcoming_tasks",
        id:   "D-05",
        name: "Upcoming Tasks",
        desc: "Active workload table · tasks starting in the next 30 days",
        ms:   "Report › Dashboards › Upcoming Tasks",
      },
    ],
  },
  {
    key:   "Resources",
    label: "Resources",
    icon:  "◈",
    color: "#2E75B6",
    description: "Resource utilisation, allocation, and overallocation reports matching MS Project's Resources category",
    reports: [
      {
        key:  "resource_overview",
        id:   "R-01",
        name: "Resource Overview",
        desc: "Work / actual work / remaining work · cost breakdown per resource",
        ms:   "Report › Resources › Resource Overview",
      },
      {
        key:  "overallocated_resources",
        id:   "R-02",
        name: "Overallocated Resources",
        desc: "Utilisation histogram · >80% flagged red · bottleneck detail table",
        ms:   "Report › Resources › Overallocated Resources",
      },
      {
        key:  "resource_stats",
        id:   "R-03",
        name: "Resource Statistics",
        desc: "Efficiency vs utilisation scatter · availability summary table",
        ms:   "View › Resource Usage · Team Planner",
      },
    ],
  },
  {
    key:   "Costs",
    label: "Costs",
    icon:  "£",
    color: "#C9A84C",
    description: "Earned value, cash flow, cost variance, and budget reports matching MS Project's Costs category",
    reports: [
      {
        key:  "earned_value",
        id:   "C-01",
        name: "Earned Value S-Curve",
        desc: "PV / EV / AC cumulative lines · EVM KPI table (CPI, SPI, VAC, EAC, TCPI)",
        ms:   "Report › Costs › Earned Value Report",
      },
      {
        key:  "cash_flow",
        id:   "C-02",
        name: "Cash Flow",
        desc: "Monthly planned vs actual spend columns · cumulative cash flow line",
        ms:   "Report › Costs › Cash Flow",
      },
      {
        key:  "cost_variance",
        id:   "C-03",
        name: "Cost Variance",
        desc: "Variance per project/task — over budget in red, under budget in green",
        ms:   "Report › Costs › Cost Variance",
      },
      {
        key:  "budget_cost",
        id:   "C-04",
        name: "Budget vs Actual",
        desc: "Budget Cost vs Actual Cost grouped bars per resource and phase",
        ms:   "Report › Costs › Budget Cost",
      },
      {
        key:  "cpi_spi_trend",
        id:   "C-05",
        name: "CPI / SPI Trend",
        desc: "CPI and SPI performance indices over time — 1.0 threshold lines",
        ms:   "Report › Costs › Earned Value (trend view)",
      },
    ],
  },
  {
    key:   "In Progress",
    label: "In Progress",
    icon:  "⧗",
    color: "#FF6600",
    description: "Schedule performance reports — critical path, late tasks, slippage, and milestones",
    reports: [
      {
        key:  "critical_tasks",
        id:   "P-01",
        name: "Critical Tasks",
        desc: "Critical path task % complete bars · slip days · detail table",
        ms:   "Report › In Progress › Critical Tasks",
      },
      {
        key:  "late_tasks",
        id:   "P-02",
        name: "Late Tasks",
        desc: "Overdue tasks sorted by days late · red bars for escalation",
        ms:   "Report › In Progress › Late Tasks",
      },
      {
        key:  "slipping_tasks",
        id:   "P-03",
        name: "Slipping Tasks",
        desc: "Schedule slippage vs baseline — 14-day amber / 30-day red thresholds",
        ms:   "Report › In Progress › Slipping Tasks",
      },
      {
        key:  "milestone_status",
        id:   "P-04",
        name: "Milestone Status",
        desc: "Status pie chart (Complete / On Track / At Risk / Delayed) · milestone register",
        ms:   "Report › In Progress › Milestone Report",
      },
    ],
  },
  {
    key:   "Visual Reports",
    label: "Visual Reports",
    icon:  "◎",
    color: "#7030A0",
    description: "Advanced analytical views — EV over time and portfolio strategic positioning (equivalent to MS Project Visual Reports and Excel OLAP PivotCharts)",
    reports: [
      {
        key:  "ev_over_time",
        id:   "V-01",
        name: "EV Over Time",
        desc: "Quarterly cumulative PV / EV / AC lines · fill between curves shows SV and CV zones",
        ms:   "Report › Visual Reports › Earned Value Over Time",
      },
      {
        key:  "portfolio_bubble",
        id:   "V-02",
        name: "Portfolio Bubble Chart",
        desc: "Strategic Value vs Risk matrix — bubble size proportional to budget",
        ms:   "Portfolio dashboard · custom visual report",
      },
    ],
  },
];

const ALL_REPORTS = CATEGORIES.flatMap(c => c.reports.map(r => ({ ...r, category: c.key, catColor: c.color })));

// ── Helpers ────────────────────────────────────────────────────────────────────
function loadHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]"); }
  catch { return []; }
}
function saveHistory(h) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(h.slice(0, MAX_HISTORY)));
}

// ── Report Card ────────────────────────────────────────────────────────────────
function ReportCard({ report, catColor, theme, currency, projectId, onGenerated }) {
  const [fmt, setFmt]       = useState("png");
  const [loading, setLoading] = useState(false);
  const [status, setStatus]   = useState(null); // "ok" | "err" | null

  const t = theme?.colors || {};
  const border   = t.border   || "#2A3A4A";
  const surface  = t.surface  || "#0D1B2A";
  const text     = t.text     || "#E0E8F0";
  const subtext  = t.subtext  || "#8AA0B0";

  async function handleGenerate() {
    setLoading(true);
    setStatus(null);
    try {
      const resp = await axios.post(
        `${API_BASE}/generate`,
        { report_key: report.key, format: fmt, project_id: projectId || null, currency },
        { responseType: "blob" }
      );
      const ext      = fmt === "xlsx" ? "xlsx" : "png";
      const mime     = fmt === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "image/png";
      const blob     = new Blob([resp.data], { type: mime });
      const url      = URL.createObjectURL(blob);
      const a        = document.createElement("a");
      const dateStr  = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const proj     = (projectId || "PORTFOLIO").toUpperCase();
      a.href         = url;
      a.download     = `${proj}_${report.name.replace(/\s+/g, "")}${dateStr}_v1.0.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      // Save to history
      const h = loadHistory();
      h.unshift({
        id:        `${Date.now()}-${report.key}`,
        key:       report.key,
        name:      report.name,
        category:  report.category,
        format:    fmt.toUpperCase(),
        project:   projectId || "Portfolio",
        currency,
        generated: new Date().toISOString(),
      });
      saveHistory(h);
      onGenerated?.();
      setStatus("ok");
    } catch (err) {
      console.error(err);
      setStatus("err");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      background:   surface,
      border:       `1px solid ${border}`,
      borderRadius: 10,
      padding:      "16px 18px",
      display:      "flex",
      flexDirection:"column",
      gap:          10,
      position:     "relative",
      transition:   "border-color 0.2s",
    }}>
      {/* ID badge */}
      <div style={{
        position:     "absolute",
        top:          10,
        right:        12,
        background:   catColor + "22",
        color:        catColor,
        border:       `1px solid ${catColor}44`,
        borderRadius: 4,
        fontSize:     10,
        fontWeight:   700,
        padding:      "1px 6px",
        letterSpacing: 0.5,
      }}>{report.id}</div>

      {/* Name */}
      <div style={{ fontWeight: 700, fontSize: 14, color: text, paddingRight: 48 }}>
        {report.name}
      </div>

      {/* Description */}
      <div style={{ fontSize: 11.5, color: subtext, lineHeight: 1.55 }}>
        {report.desc}
      </div>

      {/* MS Equivalent */}
      <div style={{
        fontSize:     10,
        color:        catColor,
        background:   catColor + "12",
        borderRadius: 4,
        padding:      "3px 7px",
        fontStyle:    "italic",
      }}>
        {report.ms}
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
        <select
          value={fmt}
          onChange={e => setFmt(e.target.value)}
          style={{
            background:   "#0A1628",
            color:        text,
            border:       `1px solid ${border}`,
            borderRadius: 5,
            padding:      "5px 8px",
            fontSize:     11,
            cursor:       "pointer",
            flex:         "0 0 auto",
          }}
        >
          <option value="png">PNG (image)</option>
          <option value="xlsx">Excel (.xlsx)</option>
        </select>

        <button
          onClick={handleGenerate}
          disabled={loading}
          style={{
            flex:         1,
            background:   loading ? "#1A2A3A" : catColor,
            color:        loading ? subtext : "#000",
            border:       "none",
            borderRadius: 5,
            padding:      "6px 14px",
            fontSize:     12,
            fontWeight:   700,
            cursor:       loading ? "not-allowed" : "pointer",
            transition:   "background 0.2s",
          }}
        >
          {loading ? "Generating…" : "⬇ Generate"}
        </button>

        {status === "ok"  && <span style={{ color: "#70AD47", fontSize: 16 }}>✔</span>}
        {status === "err" && <span style={{ color: "#FF4444", fontSize: 13 }}>✗ Error</span>}
      </div>
    </div>
  );
}

// ── History Row ────────────────────────────────────────────────────────────────
function HistoryRow({ entry, t }) {
  const d = new Date(entry.generated);
  const timeStr = d.toLocaleString();
  const fmtColor = entry.format === "XLSX" ? "#70AD47" : "#2E75B6";
  return (
    <div style={{
      display:       "grid",
      gridTemplateColumns: "1fr 90px 80px 90px 140px",
      gap:           8,
      padding:       "7px 12px",
      borderBottom:  `1px solid ${t.border}`,
      fontSize:      12,
      alignItems:    "center",
    }}>
      <span style={{ color: t.text, fontWeight: 600 }}>{entry.name}</span>
      <span style={{ color: t.subtext }}>{entry.category}</span>
      <span style={{ color: fmtColor, fontWeight: 700 }}>{entry.format}</span>
      <span style={{ color: t.subtext }}>{entry.project}</span>
      <span style={{ color: t.subtext, fontSize: 11 }}>{timeStr}</span>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function ReportsCenter() {
  const { theme }                = useTheme();
  const [activeTab, setActiveTab]  = useState("Dashboards");
  const [viewMode, setViewMode]    = useState("generate"); // "generate" | "history"
  const [history, setHistory]      = useState(loadHistory());
  const [currency, setCurrency]    = useState("GBP");
  const [projectId, setProjectId]  = useState("");
  const [search, setSearch]        = useState("");
  const [serverOk, setServerOk]    = useState(null);

  const t = theme?.colors || {};
  const bg        = t.background || "#060E1A";
  const surface   = t.surface    || "#0D1B2A";
  const border    = t.border     || "#2A3A4A";
  const text      = t.text       || "#E0E8F0";
  const subtext   = t.subtext    || "#8AA0B0";
  const accent    = "#C9A84C";

  // Check server health once on mount
  useEffect(() => {
    axios.get(`${API_BASE}/catalog`)
      .then(() => setServerOk(true))
      .catch(() => setServerOk(false));
  }, []);

  const refreshHistory = useCallback(() => {
    setHistory(loadHistory());
  }, []);

  // Filter reports by search
  const filteredReports = useCallback((reports) => {
    if (!search.trim()) return reports;
    const q = search.toLowerCase();
    return reports.filter(r =>
      r.name.toLowerCase().includes(q) ||
      r.desc.toLowerCase().includes(q) ||
      r.id.toLowerCase().includes(q)
    );
  }, [search]);

  const activeCategory = CATEGORIES.find(c => c.key === activeTab);
  const searchMode = search.trim().length > 0;
  const matchingAll = searchMode ? ALL_REPORTS.filter(r => {
    const q = search.toLowerCase();
    return r.name.toLowerCase().includes(q) || r.desc.toLowerCase().includes(q) || r.id.toLowerCase().includes(q);
  }) : [];

  return (
    <div style={{ background: bg, minHeight: "100vh", color: text, padding: "28px 32px", fontFamily: "Inter, system-ui, sans-serif" }}>

      {/* Page header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10,
              background: "linear-gradient(135deg, #1F3864, #2E75B6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 22,
            }}>◇</div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: text }}>
                MS Project Reports Centre
              </h1>
              <p style={{ margin: "2px 0 0", fontSize: 12.5, color: subtext }}>
                All 19 Microsoft Project 2024 charts, dashboards & visual reports — generated as PNG or Excel
              </p>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {/* Server status */}
          <div style={{
            padding:      "5px 12px",
            borderRadius: 6,
            fontSize:     11,
            fontWeight:   600,
            background:   serverOk === null ? "#1A2A3A" : serverOk ? "#70AD4722" : "#FF000022",
            color:        serverOk === null ? subtext : serverOk ? "#70AD47" : "#FF4444",
            border:       `1px solid ${serverOk === null ? border : serverOk ? "#70AD47" : "#FF4444"}`,
          }}>
            {serverOk === null ? "⟳ Checking…" : serverOk ? "✔ Report engine online" : "✗ Engine offline — start backend"}
          </div>

          {/* Currency */}
          <select
            value={currency}
            onChange={e => setCurrency(e.target.value)}
            style={{ background: surface, color: text, border: `1px solid ${border}`, borderRadius: 6, padding: "6px 10px", fontSize: 12 }}
          >
            <option value="GBP">£ GBP</option>
            <option value="USD">$ USD</option>
            <option value="EUR">€ EUR</option>
            <option value="AED">AED</option>
          </select>

          {/* Project filter */}
          <input
            type="text"
            placeholder="Project ID (optional)…"
            value={projectId}
            onChange={e => setProjectId(e.target.value)}
            style={{
              background: surface, color: text,
              border: `1px solid ${border}`, borderRadius: 6,
              padding: "6px 10px", fontSize: 12, width: 160,
            }}
          />
        </div>
      </div>

      {/* Tab bar: Generate / History */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${border}`, paddingBottom: 0 }}>
        {[
          { id: "generate", label: `Generate Reports (19)` },
          { id: "history",  label: `Report History (${history.length})` },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setViewMode(tab.id)}
            style={{
              background:  viewMode === tab.id ? accent : "transparent",
              color:       viewMode === tab.id ? "#000" : subtext,
              border:      "none",
              borderRadius: "6px 6px 0 0",
              padding:     "9px 20px",
              fontSize:    12.5,
              fontWeight:  700,
              cursor:      "pointer",
            }}
          >{tab.label}</button>
        ))}
      </div>

      {/* ── HISTORY VIEW ─────────────────────────────────────────────────────── */}
      {viewMode === "history" && (
        <div style={{ background: surface, borderRadius: 10, border: `1px solid ${border}`, overflow: "hidden" }}>
          {history.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: subtext }}>
              No reports generated yet. Use the Generate tab to create your first report.
            </div>
          ) : (
            <>
              {/* Header row */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 90px 80px 90px 140px",
                gap: 8, padding: "8px 12px",
                background: "#1F3864",
                fontSize: 11, fontWeight: 700, color: "#BDD7EE",
              }}>
                <span>Report Name</span>
                <span>Category</span>
                <span>Format</span>
                <span>Project</span>
                <span>Generated</span>
              </div>
              {history.map(entry => (
                <HistoryRow key={entry.id} entry={entry} t={{ text, subtext, border }} />
              ))}
            </>
          )}
          {history.length > 0 && (
            <div style={{ padding: "10px 12px", borderTop: `1px solid ${border}` }}>
              <button
                onClick={() => { localStorage.removeItem(HISTORY_KEY); setHistory([]); }}
                style={{
                  background: "transparent", color: "#FF4444",
                  border: "1px solid #FF444444", borderRadius: 5,
                  padding: "4px 12px", fontSize: 11, cursor: "pointer",
                }}
              >Clear History</button>
            </div>
          )}
        </div>
      )}

      {/* ── GENERATE VIEW ────────────────────────────────────────────────────── */}
      {viewMode === "generate" && (
        <>
          {/* Search bar */}
          <div style={{ marginBottom: 18 }}>
            <input
              type="text"
              placeholder="Search all 19 reports by name, description, or ID (D-01, C-03…)"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: "100%", boxSizing: "border-box",
                background: surface, color: text,
                border: `1px solid ${border}`, borderRadius: 7,
                padding: "9px 14px", fontSize: 13,
              }}
            />
          </div>

          {/* Search results mode */}
          {searchMode ? (
            <>
              <div style={{ marginBottom: 14, color: subtext, fontSize: 12 }}>
                {matchingAll.length} result{matchingAll.length !== 1 ? "s" : ""} for "{search}"
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: 14,
              }}>
                {matchingAll.map(r => (
                  <ReportCard
                    key={r.key}
                    report={r}
                    catColor={r.catColor}
                    theme={theme}
                    currency={currency}
                    projectId={projectId}
                    onGenerated={refreshHistory}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Category tabs */}
              <div style={{
                display: "flex", gap: 6, marginBottom: 22, flexWrap: "wrap",
              }}>
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => setActiveTab(cat.key)}
                    style={{
                      background:   activeTab === cat.key ? cat.color : surface,
                      color:        activeTab === cat.key ? "#fff" : subtext,
                      border:       `1px solid ${activeTab === cat.key ? cat.color : border}`,
                      borderRadius: 7,
                      padding:      "7px 16px",
                      fontSize:     12,
                      fontWeight:   700,
                      cursor:       "pointer",
                      transition:   "all 0.15s",
                    }}
                  >
                    {cat.icon} {cat.label}
                    <span style={{
                      marginLeft:   6,
                      background:   activeTab === cat.key ? "rgba(255,255,255,0.25)" : border,
                      color:        activeTab === cat.key ? "#fff" : subtext,
                      borderRadius: 10,
                      padding:      "1px 7px",
                      fontSize:     10,
                    }}>
                      {cat.reports.length}
                    </span>
                  </button>
                ))}
              </div>

              {/* Category description */}
              {activeCategory && (
                <div style={{
                  marginBottom: 18,
                  padding:      "10px 14px",
                  background:   activeCategory.color + "12",
                  border:       `1px solid ${activeCategory.color}33`,
                  borderRadius: 7,
                  fontSize:     12.5,
                  color:        subtext,
                }}>
                  <span style={{ color: activeCategory.color, fontWeight: 700 }}>
                    {activeCategory.icon} {activeCategory.label} ({activeCategory.reports.length} reports)
                  </span>
                  {"  —  "}{activeCategory.description}
                </div>
              )}

              {/* Report grid */}
              {activeCategory && (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: 14,
                }}>
                  {filteredReports(activeCategory.reports).map(r => (
                    <ReportCard
                      key={r.key}
                      report={r}
                      catColor={activeCategory.color}
                      theme={theme}
                      currency={currency}
                      projectId={projectId}
                      onGenerated={refreshHistory}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Footer note */}
          <div style={{
            marginTop: 36,
            padding:   "12px 16px",
            background: surface,
            border:     `1px solid ${border}`,
            borderRadius: 7,
            fontSize:   11.5,
            color:      subtext,
            lineHeight: 1.7,
          }}>
            <strong style={{ color: accent }}>Output formats:</strong>{" "}
            PNG (150 dpi, ready to embed in presentations) or Excel (.xlsx — chart image on
            Sheet 1 + Report Info metadata sheet).{" "}
            <strong style={{ color: accent }}>Corporate palette:</strong>{" "}
            Navy #1F3864 · Gold #C9A84C · Mid Blue #2E75B6 — matches MS Project 2024 output.{" "}
            <strong style={{ color: accent }}>EVM fields:</strong>{" "}
            All EV/AC/PV values are synthesised from your live portfolio CPI, SPI, and budget
            data in the registry.{" "}
            <strong style={{ color: accent }}>Backend required:</strong>{" "}
            <code style={{ background: "#1A2A3A", padding: "1px 5px", borderRadius: 3 }}>
              uvicorn main:app --reload
            </code>{" "}
            in the backend folder.
          </div>
        </>
      )}
    </div>
  );
}


